import { apiClient } from './api';
import type { ArtworkResponse } from './artworkService';

export interface ExhibitionResponse {
  exhibition_id: number;
  title: string;
  description?: string | null;
  is_featured: boolean;
  created_at: string;
  tags?: string[];
  artwork_count?: number;
}

export interface ExhibitionWithArtworks extends ExhibitionResponse {
  artworks: Array<{
    artwork_id: number;
    student_name?: string;
    uploaded_at: string;
  }>;
}

export interface ExhibitionArtworkPageResponse {
  artworks: ArtworkResponse[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateExhibitionRequest {
  title: string;
  description?: string;
  is_featured?: boolean;
  tag_ids?: number[];
}

export interface UpdateExhibitionRequest {
  title?: string;
  description?: string;
  is_featured?: boolean;
  tag_ids?: number[];
}

export interface ExhibitionFilters {
  is_featured?: boolean;
  excludeFeatured?: boolean;
  tags?: string[];
  search?: string;
  created_after?: string;
  created_before?: string;
}

export interface ExhibitionListResponse {
  exhibitions: ExhibitionResponse[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface FeaturedExhibitionError {
  message: string;
  code: 'FEATURED_LIMIT_EXCEEDED' | 'EXHIBITION_NOT_FOUND' | 'ALREADY_FEATURED';
  currentFeaturedCount?: number;
  maxFeaturedCount?: number;
}

export class ExhibitionService {
  async getExhibitions(
    page = 1,
    limit = 20,
    filters?: ExhibitionFilters,
    includeArtworkCount = false,
    admin = false,
  ): Promise<ExhibitionListResponse> {
    const params = new URLSearchParams({
      limit: limit.toString(),
      offset: ((page - 1) * limit).toString(), // Convert page to offset
      includeArtworkCount: includeArtworkCount.toString(),
    });

    if (filters) {
      if (filters.is_featured !== undefined) {
        params.append('featuredOnly', filters.is_featured.toString()); // Use featuredOnly instead of is_featured
      }
      if (filters.excludeFeatured !== undefined) {
        params.append('excludeFeatured', filters.excludeFeatured.toString());
      }
      if (filters.tags && filters.tags.length > 0) {
        filters.tags.forEach(tag => params.append('tags[]', tag));
      }
      if (filters.search) {
        params.append('search', filters.search);
      }
      if (filters.created_after) {
        params.append('created_after', filters.created_after);
      }
      if (filters.created_before) {
        params.append('created_before', filters.created_before);
      }
    }

    // Backend returns data differently, so we need to adapt the response
    const response = await apiClient.get<{
      message: string;
      data: ExhibitionResponse[];
      pagination: {
        total: number;
        offset: number;
        limit: number;
        hasMore: boolean;
      };
    }>(`${admin ? '/exhibitions/admin' : '/exhibitions'}?${params}`);

    // Transform backend response to match frontend expectations
    return {
      exhibitions: response.data,
      total: response.pagination.total,
      page: Math.floor(response.pagination.offset / response.pagination.limit) + 1,
      limit: response.pagination.limit,
      totalPages: Math.ceil(response.pagination.total / response.pagination.limit),
    };
  }

  async getAdminExhibitions(
    page = 1,
    limit = 20,
    filters?: ExhibitionFilters,
    includeArtworkCount = false,
  ): Promise<ExhibitionListResponse> {
    return this.getExhibitions(page, limit, filters, includeArtworkCount, true);
  }

  async getExhibition(id: number, includeArtworkCount = true, admin = false): Promise<ExhibitionResponse> {
    const response = await apiClient.get<{
      message: string;
      data: ExhibitionResponse;
    }>(`${admin ? `/exhibitions/admin/${id}` : `/exhibitions/${id}`}?includeArtworkCount=${includeArtworkCount}`);
    return response.data;
  }

  async getAdminExhibition(id: number, includeArtworkCount = true): Promise<ExhibitionResponse> {
    return this.getExhibition(id, includeArtworkCount, true);
  }

  async getExhibitionArtworks(id: number, limit = 6, admin = false): Promise<ArtworkResponse[]> {
    const response = await apiClient.get<{
      message: string;
      data: ArtworkResponse[];
    }>(`${admin ? `/exhibitions/admin/${id}/artworks` : `/exhibitions/${id}/artworks`}?limit=${limit}`);
    return response.data || [];
  }

  async getAdminExhibitionArtworks(id: number, limit = 6): Promise<ArtworkResponse[]> {
    return this.getExhibitionArtworks(id, limit, true);
  }

  async getExhibitionArtworkPage(
    id: number,
    page = 1,
    limit = 20,
    admin = false,
  ): Promise<ExhibitionArtworkPageResponse> {
    const response = await apiClient.get<{
      message: string;
      data: ArtworkResponse[];
      pagination: {
        total: number;
        page: number;
        limit: number;
        totalPages: number;
      };
    }>(`${admin ? `/exhibitions/admin/${id}/artworks` : `/exhibitions/${id}/artworks`}?page=${page}&limit=${limit}`);

    return {
      artworks: response.data,
      total: response.pagination.total,
      page: response.pagination.page,
      limit: response.pagination.limit,
      totalPages: response.pagination.totalPages,
    };
  }

  async getAdminExhibitionArtworkPage(id: number, page = 1, limit = 20): Promise<ExhibitionArtworkPageResponse> {
    return this.getExhibitionArtworkPage(id, page, limit, true);
  }

  async createExhibition(exhibition: CreateExhibitionRequest): Promise<ExhibitionResponse> {
    return apiClient.post<ExhibitionResponse>('/exhibitions/admin', exhibition);
  }

  async updateExhibition(id: number, updates: UpdateExhibitionRequest): Promise<ExhibitionResponse> {
    return apiClient.put<ExhibitionResponse>(`/exhibitions/admin/${id}`, updates);
  }

  async deleteExhibition(id: number): Promise<void> {
    await apiClient.delete(`/exhibitions/admin/${id}`);
  }

  async addTagsToExhibition(exhibitionId: number, tagIds: number[]): Promise<ExhibitionResponse> {
    return apiClient.post<ExhibitionResponse>(`/exhibitions/${exhibitionId}/tags`, {
      tag_ids: tagIds,
    });
  }

  async removeTagsFromExhibition(exhibitionId: number, tagIds: number[]): Promise<ExhibitionResponse> {
    return apiClient.delete<ExhibitionResponse>(`/exhibitions/${exhibitionId}/tags`, {
      data: { tag_ids: tagIds },
    });
  }

  async replaceExhibitionTags(exhibitionId: number, tagIds: number[]): Promise<ExhibitionResponse> {
    return apiClient.put<ExhibitionResponse>(`/exhibitions/${exhibitionId}/tags`, {
      tag_ids: tagIds,
    });
  }

  // Featured exhibitions management
  async getFeaturedExhibitions(admin = false): Promise<ExhibitionResponse[]> {
    try {
      // Use the new dedicated featured exhibitions endpoint that returns up to 5
      const response = await apiClient.get<{
        message: string;
        data: ExhibitionResponse[];
      }>(admin ? '/exhibitions/admin/featured' : '/exhibitions/featured');
      return response.data || [];
    } catch (error: any) {
      console.warn('Failed to fetch featured exhibitions:', error);
      // Return empty array on any error to prevent component crashes
      return [];
    }
  }

  async getAdminFeaturedExhibitions(): Promise<ExhibitionResponse[]> {
    return this.getFeaturedExhibitions(true);
  }

  async setFeaturedExhibition(id: number): Promise<ExhibitionResponse> {
    try {
      return await apiClient.put<ExhibitionResponse>(`/exhibitions/admin/${id}/featured`);
    } catch (error: any) {
      // Handle featured exhibition limit validation errors
      if (error.status === 400 && error.message?.includes('Cannot feature more than')) {
        const featuredError: FeaturedExhibitionError = {
          message: error.message,
          code: 'FEATURED_LIMIT_EXCEEDED',
          maxFeaturedCount: 5,
          currentFeaturedCount: error.errors?.currentCount || undefined
        };
        throw { ...error, featuredError };
      }
      throw error;
    }
  }

  async unsetFeaturedExhibition(id: number): Promise<ExhibitionResponse> {
    // Use the proper unfeature endpoint instead of generic update
    await this.unfeatureExhibition(id);
    // Return the updated exhibition data
    return this.getExhibition(id) as Promise<ExhibitionResponse>;
  }

  async unfeatureExhibition(id: number): Promise<void> {
    // New method for admin to unfeature individual exhibitions
    await apiClient.delete(`/exhibitions/admin/${id}/featured`);
  }

  // Helper methods for featured exhibition management
  async getFeaturedExhibitionCount(): Promise<number> {
    const featured = await this.getFeaturedExhibitions();
    return featured.length;
  }

  async canFeatureMoreExhibitions(): Promise<boolean> {
    const count = await this.getFeaturedExhibitionCount();
    return count < 5;
  }

  async getRemainingFeaturedSlots(): Promise<number> {
    const count = await this.getFeaturedExhibitionCount();
    return Math.max(0, 5 - count);
  }

  async getFeaturedExhibition(admin = false): Promise<ExhibitionResponse | null> {
    try {
      // Use the backwards compatibility endpoint for single featured exhibition
      const response = await apiClient.get<{
        message: string;
        data: ExhibitionResponse;
      }>(admin ? '/exhibitions/admin/featured/first' : '/exhibitions/featured/first');
      return response.data;
    } catch (error: any) {
      // If no featured exhibition exists, API returns 404
      if (error.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async getAdminFeaturedExhibition(): Promise<ExhibitionResponse | null> {
    return this.getFeaturedExhibition(true);
  }

  // Search and filtering helpers
  async searchExhibitions(query: string, limit = 10): Promise<ExhibitionResponse[]> {
    const response = await this.getExhibitions(1, limit, { search: query });
    return response.exhibitions;
  }

  async getExhibitionsByTags(tags: string[], page = 1, limit = 20): Promise<ExhibitionListResponse> {
    return this.getExhibitions(page, limit, { tags });
  }

  async getRecentExhibitions(limit = 10): Promise<ExhibitionResponse[]> {
    const response = await this.getExhibitions(1, limit);
    return response.exhibitions;
  }

  // Exhibition curation helpers
  async getArtworkCountByTags(tagIds: number[]): Promise<{
    tag_combinations: Array<{
      tags: string[];
      count: number;
    }>;
    total_count: number;
  }> {
    return apiClient.post('/exhibitions/preview-artworks', { tag_ids: tagIds });
  }

  async previewExhibitionArtworks(tagIds: number[], limit = 20): Promise<Array<{
    artwork_id: number;
    student_name?: string;
    uploaded_at: string;
    tags: string[];
  }>> {
    return apiClient.post(`/exhibitions/preview-artworks?limit=${limit}`, { tag_ids: tagIds });
  }

  // Statistics and analytics
  async getExhibitionStats(): Promise<{
    total_exhibitions: number;
    featured_count: number;
    exhibitions_this_month: number;
    avg_artworks_per_exhibition: number;
    most_used_tags: Array<{
      tag: string;
      usage_count: number;
    }>;
  }> {
    return apiClient.get('/exhibitions/admin/stats');
  }

  async getExhibitionAnalytics(id: number): Promise<{
    exhibition_id: number;
    artwork_count: number;
    unique_students: number;
    tag_distribution: Array<{
      tag: string;
      count: number;
    }>;
    creation_timeline: Array<{
      date: string;
      count: number;
    }>;
  }> {
    return apiClient.get(`/exhibitions/${id}/analytics`);
  }

  // Validation helpers
  validateExhibitionData(data: CreateExhibitionRequest): {
    valid: boolean;
    errors: Record<string, string>;
  } {
    const errors: Record<string, string> = {};

    if (!data.title || data.title.trim().length === 0) {
      errors.title = 'Title is required';
    } else if (data.title.trim().length > 100) {
      errors.title = 'Title must be less than 100 characters';
    }

    if (data.description && data.description.length > 500) {
      errors.description = 'Description must be less than 500 characters';
    }

    if (data.tag_ids && data.tag_ids.length === 0) {
      errors.tag_ids = 'At least one tag must be selected';
    }

    return {
      valid: Object.keys(errors).length === 0,
      errors,
    };
  }

  // Utility methods
  formatExhibitionDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }

  formatExhibitionDateShort(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }

  generateExhibitionSlug(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  }

  // Bulk operations
  async bulkUpdateExhibitions(
    updates: Array<{ id: number; data: UpdateExhibitionRequest }>
  ): Promise<Array<{ success: boolean; exhibition?: ExhibitionResponse; error?: string }>> {
    const results = await Promise.allSettled(
      updates.map(({ id, data }) => this.updateExhibition(id, data))
    );

    return results.map((result) => {
      if (result.status === 'fulfilled') {
        return { success: true, exhibition: result.value };
      } else {
        return {
          success: false,
          error: result.reason?.message || 'Unknown error occurred',
        };
      }
    });
  }

  async bulkDeleteExhibitions(ids: number[]): Promise<{
    success: number;
    failed: number;
    errors: Array<{ id: number; error: string }>;
  }> {
    const results = await Promise.allSettled(
      ids.map(id => this.deleteExhibition(id))
    );

    let successCount = 0;
    const errors: Array<{ id: number; error: string }> = [];

    results.forEach((result, index) => {
      if (result.status === 'fulfilled') {
        successCount++;
      } else {
        errors.push({
          id: ids[index],
          error: result.reason?.message || 'Unknown error occurred',
        });
      }
    });

    return {
      success: successCount,
      failed: errors.length,
      errors,
    };
  }
}

export const exhibitionService = new ExhibitionService();
export default exhibitionService;
